import clsx from "clsx";
import {
  Cat,
  PawPrint,
  Plus,
  Settings,
  DollarSign,
  GitBranch,
  Clock,
  Server,
  Search,
  ArrowUpDown,
  Skull,
  GitPullRequest,
  AlertTriangle,
} from "lucide-react";
import { useState, useMemo, useRef, useEffect } from "react";

import { useMoriartyEnvironments, useMoriartyProntoStatuses } from "@/hooks/useMoriarty";
import {
  compareMoriartyStatuses,
  getMoriartyDisplayStatus,
  isMoriartyProntoEligible,
} from "@/lib/moriarty";
import { useStore } from "@/lib/store";
import type { MoriartyEnvironment, MoriartyProntoSource, MoriartyProntoStatus } from "@/lib/types";
import { formatUptime, formatCost } from "@/lib/utils";

import { PageViewHeader } from "../PageViewHeader";
import { MoriartySpawnModal } from "./MoriartySpawnModal";

type ViewTab = "active" | "all" | "terminated";

type ProntoEnrichment = {
  byPrNumber: Map<number, MoriartyProntoStatus>;
  source: MoriartyProntoSource | null;
  isLoading: boolean;
  isError: boolean;
};

// Status config
function statusMeta(status: ReturnType<typeof getMoriartyDisplayStatus>) {
  switch (status) {
    case "starting":
      return {
        label: "BOOTING",
        color: "text-cyber-yellow",
        bg: "bg-cyber-yellow/10",
        border: "border-cyber-yellow/30",
        dotColor: "bg-cyber-yellow",
        hex: "#ffff00",
        hexDim: "rgba(255,255,0,0.15)",
        glowClass: "moriarty-node-glow-yellow",
      };
    case "ready":
      return {
        label: "ONLINE",
        color: "text-cyber-green",
        bg: "bg-cyber-green/10",
        border: "border-cyber-green/30",
        dotColor: "bg-cyber-green",
        hex: "#00ff88",
        hexDim: "rgba(0,255,136,0.15)",
        glowClass: "moriarty-node-glow-green",
      };
    case "warning":
      return {
        label: "IDLE",
        color: "text-cyber-yellow",
        bg: "bg-cyber-yellow/10",
        border: "border-cyber-yellow/30",
        dotColor: "bg-cyber-yellow",
        hex: "#ffff00",
        hexDim: "rgba(255,255,0,0.15)",
        glowClass: "moriarty-node-glow-yellow",
      };
    case "stopping":
      return {
        label: "SLEEPING",
        color: "text-cyber-yellow",
        bg: "bg-cyber-yellow/10",
        border: "border-cyber-yellow/30",
        dotColor: "bg-cyber-yellow",
        hex: "#ffff00",
        hexDim: "rgba(255,255,0,0.15)",
        glowClass: "moriarty-node-glow-yellow",
      };
    case "stopped":
      return {
        label: "ASLEEP",
        color: "text-cyber-muted",
        bg: "bg-cyber-muted/10",
        border: "border-cyber-border",
        dotColor: "bg-cyber-muted/50",
        hex: "#6b7280",
        hexDim: "rgba(107,114,128,0.1)",
        glowClass: "",
      };
    case "resuming":
      return {
        label: "WAKING",
        color: "text-cyber-cyan",
        bg: "bg-cyber-cyan/10",
        border: "border-cyber-cyan/30",
        dotColor: "bg-cyber-cyan",
        hex: "#00e5ff",
        hexDim: "rgba(0,229,255,0.15)",
        glowClass: "moriarty-node-glow-cyan",
      };
    case "terminated":
      return {
        label: "OFFLINE",
        color: "text-cyber-muted",
        bg: "bg-cyber-muted/10",
        border: "border-cyber-border",
        dotColor: "bg-cyber-muted/50",
        hex: "#6b7280",
        hexDim: "rgba(107,114,128,0.1)",
        glowClass: "",
      };
    case "boot_failed":
    case "resume_failed":
    case "failed":
      return {
        label:
          status === "resume_failed"
            ? "RESUME FAILED"
            : status === "boot_failed"
              ? "BOOT FAILED · BILLING"
              : "FAILED",
        color: "text-cyber-red",
        bg: "bg-cyber-red/10",
        border: "border-cyber-red/30",
        dotColor: "bg-cyber-red",
        hex: "#ff0055",
        hexDim: "rgba(255,0,85,0.15)",
        glowClass: "moriarty-node-glow-red",
      };
  }
}

// ── Topology layout math ────────────────────────────────────

type NodeLayout = {
  env: MoriartyEnvironment;
  x: number;
  y: number;
  orbitRadius: number;
  angle: number;
};

function computeLayout(
  environments: MoriartyEnvironment[],
  containerW: number,
  containerH: number,
): { nodes: NodeLayout[]; cx: number; cy: number; orbits: number[] } {
  const cx = containerW / 2;
  const cy = containerH / 2;

  // Group by status for orbital placement
  const booting = environments.filter((e) =>
    ["starting", "stopping", "resuming"].includes(getMoriartyDisplayStatus(e)),
  );
  const online = environments.filter((e) => e.status === "ready");
  const idle = environments.filter((e) => e.status === "warning" || e.status === "stopped");
  const failed = environments.filter((e) =>
    ["failed", "boot_failed", "resume_failed"].includes(getMoriartyDisplayStatus(e)),
  );

  // Orbital radii — scale based on available space
  const maxR = Math.min(cx, cy) - 60;
  const orbits = [
    maxR * 0.35, // inner orbit: booting
    maxR * 0.62, // middle orbit: online
    maxR * 0.88, // outer orbit: idle + failed
  ];

  const nodes: NodeLayout[] = [];

  function placeRing(envs: MoriartyEnvironment[], radius: number, startAngle: number) {
    const count = envs.length;
    if (count === 0) return;
    // Spread evenly; add a slight per-ring offset so orbits don't align
    const step = (2 * Math.PI) / Math.max(count, 1);
    envs.forEach((env, i) => {
      const angle = startAngle + i * step;
      // Add slight wobble so it doesn't look too mechanical
      const wobble = Math.sin(i * 2.7) * 0.08 * radius;
      const r = radius + wobble;
      nodes.push({
        env,
        x: cx + Math.cos(angle) * r,
        y: cy + Math.sin(angle) * r,
        orbitRadius: radius,
        angle,
      });
    });
  }

  placeRing(booting, orbits[0], -Math.PI / 2);
  placeRing(online, orbits[1], -Math.PI / 3);
  placeRing([...idle, ...failed], orbits[2], -Math.PI / 4);

  return { nodes, cx, cy, orbits };
}

// ── Main Component ──────────────────────────────────────────

export function MoriartyList(): React.JSX.Element {
  const selectMoriartyEnv = useStore((state) => state.selectMoriartyEnv);
  const setView = useStore((state) => state.setView);
  const [showSpawn, setShowSpawn] = useState(false);
  const [viewTab, setViewTab] = useState<ViewTab>("active");

  const { data, isLoading } = useMoriartyEnvironments({
    includeTerminated: viewTab === "all" || viewTab === "terminated",
  });

  const environments = data?.environments ?? [];
  const totalCost = data?.totalEstimatedCostUsd ?? 0;
  const hasProntoEligibleEnvironment = useMemo(
    () => environments.some(isMoriartyProntoEligible),
    [environments],
  );
  const prontoQuery = useMoriartyProntoStatuses(hasProntoEligibleEnvironment);
  const prontoByPrNumber = useMemo(
    () => new Map(prontoQuery.data?.statuses.map((status) => [status.prNumber, status]) ?? []),
    [prontoQuery.data],
  );
  const pronto: ProntoEnrichment = {
    byPrNumber: prontoByPrNumber,
    source: prontoQuery.data?.source ?? null,
    isLoading: prontoQuery.isLoading,
    isError: prontoQuery.isError,
  };

  const nonTerminated = useMemo(
    () => environments.filter((e) => e.status !== "terminated" && e.status !== "failed"),
    [environments],
  );
  const activeCount = nonTerminated.filter(
    (environment) => environment.status !== "stopped",
  ).length;
  const stoppedCount = nonTerminated.filter(
    (environment) => environment.status === "stopped",
  ).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageViewHeader
        title="Moriarty"
        badge={
          <>
            {activeCount > 0 ? (
              <span className="border-cyber-magenta/20 bg-cyber-magenta/10 text-cyber-magenta rounded-full border px-1.5 py-0.5 text-[10px]">
                {activeCount} live
              </span>
            ) : null}
            {stoppedCount > 0 ? (
              <span className="border-cyber-border bg-cyber-surface text-cyber-muted rounded-full border px-1.5 py-0.5 text-[10px]">
                {stoppedCount} asleep
              </span>
            ) : null}
            {totalCost > 0 ? (
              <span className="bg-cyber-surface text-cyber-muted flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px]">
                <DollarSign size={8} />
                {formatCost(totalCost)}
              </span>
            ) : null}
          </>
        }
        actions={
          <>
            <button
              onClick={() => {
                setView("moriarty-costs");
                const url = new URL(window.location.href);
                url.searchParams.set("view", "moriarty-costs");
                window.history.pushState({}, "", url);
              }}
              className="text-cyber-muted hover:bg-cyber-magenta/10 hover:text-cyber-magenta rounded p-1.5 transition-all"
              title="Cost Analysis"
            >
              <DollarSign size={14} />
            </button>
            <button
              onClick={() => {
                setView("moriarty-config");
                const url = new URL(window.location.href);
                url.searchParams.set("view", "moriarty-config");
                window.history.pushState({}, "", url);
              }}
              className="text-cyber-muted hover:bg-cyber-magenta/10 hover:text-cyber-magenta rounded p-1.5 transition-all"
              title="Configuration"
            >
              <Settings size={14} />
            </button>
            <button
              onClick={() => setShowSpawn(true)}
              className="border-cyber-magenta/30 text-cyber-magenta hover:bg-cyber-magenta/10 flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all"
            >
              <Plus size={14} />
              Deploy
            </button>
          </>
        }
      />

      {/* Tabs */}
      <div className="border-cyber-border flex flex-shrink-0 items-center gap-1 border-b px-6 py-2">
        {[
          { key: "active" as ViewTab, label: "Active" },
          { key: "all" as ViewTab, label: "All" },
          { key: "terminated" as ViewTab, label: "Terminated" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setViewTab(t.key)}
            className={clsx(
              "rounded px-2.5 py-1 text-[10px] tracking-wider uppercase transition-all",
              viewTab === t.key
                ? "bg-cyber-magenta/10 text-cyber-magenta"
                : "text-cyber-muted hover:text-cyber-text",
            )}
          >
            {t.label}
          </button>
        ))}
        <div className="text-cyber-magenta/15 ml-auto flex items-center gap-1">
          <PawPrint size={10} />
          <PawPrint size={10} style={{ transform: "rotate(15deg)" }} />
          <PawPrint size={10} style={{ transform: "rotate(-10deg)" }} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-cyber-muted py-12 text-center">
            <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
            Scanning network...
          </div>
        ) : viewTab === "active" ? (
          <ActiveView
            environments={nonTerminated}
            pronto={pronto}
            onSelect={selectMoriartyEnv}
            onSpawn={() => setShowSpawn(true)}
          />
        ) : (
          <EnvironmentTable
            environments={
              viewTab === "terminated"
                ? environments.filter((e) => e.status === "terminated")
                : environments
            }
            pronto={pronto}
            onSelect={selectMoriartyEnv}
          />
        )}
      </div>

      {showSpawn && <MoriartySpawnModal onClose={() => setShowSpawn(false)} />}
    </div>
  );
}

// ── Active View — Network Topology ──────────────────────────

function ActiveView({
  environments,
  pronto,
  onSelect,
  onSpawn,
}: {
  environments: MoriartyEnvironment[];
  pronto: ProntoEnrichment;
  onSelect: (id: string) => void;
  onSpawn: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 800, h: 500 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Measure container
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setDims({ w: width, h: height });
        }
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (environments.length === 0) {
    return (
      <div className="py-16 text-center">
        <Cat size={40} className="text-cyber-muted/15 mx-auto mb-4" />
        <p className="text-cyber-muted mb-1 text-sm">The network is dormant</p>
        <p className="text-cyber-muted/60 mb-6 text-xs">No Moriartys deployed.</p>
        <button
          onClick={onSpawn}
          className="border-cyber-magenta/30 text-cyber-magenta hover:bg-cyber-magenta/10 rounded border px-4 py-2 text-xs transition-all"
        >
          Deploy first Moriarty
        </button>
      </div>
    );
  }

  const { nodes, cx, cy, orbits } = computeLayout(environments, dims.w, dims.h);

  return (
    <div ref={containerRef} className="moriarty-topology-bg relative h-full min-h-[450px] w-full">
      {/* SVG layer — orbital rings, connection lines, particles */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox={`0 0 ${dims.w} ${dims.h}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Magenta glow filter for hub */}
          <filter id="moriarty-hub-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Grid pattern background */}
        <pattern
          id="moriarty-grid"
          x="0"
          y="0"
          width="40"
          height="40"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M 40 0 L 0 0 0 40"
            fill="none"
            stroke="rgba(237,106,255,0.03)"
            strokeWidth="0.5"
          />
        </pattern>
        <rect width="100%" height="100%" fill="url(#moriarty-grid)" />

        {/* Orbital rings */}
        {orbits.map((r, i) => (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(237,106,255,0.06)"
            strokeWidth="1"
            strokeDasharray="4 8"
            className="moriarty-orbit-ring"
            style={{ animationDelay: `${i * -5}s` }}
          />
        ))}

        {/* Connection lines from hub to each node */}
        {nodes.map((node) => {
          const meta = statusMeta(getMoriartyDisplayStatus(node.env));
          const isHovered = hoveredId === node.env.id;
          return (
            <g key={`line-${node.env.id}`}>
              {/* Main connection line */}
              <line
                x1={cx}
                y1={cy}
                x2={node.x}
                y2={node.y}
                stroke={isHovered ? meta.hex : "rgba(237,106,255,0.08)"}
                strokeWidth={isHovered ? 1.5 : 0.5}
                className="transition-all duration-300"
              />
              {/* Animated data packet traveling along the line */}
              {node.env.status !== "failed" && (
                <circle r="2" fill={meta.hex} opacity="0.7">
                  <animateMotion
                    dur={`${2 + Math.random() * 2}s`}
                    repeatCount="indefinite"
                    path={`M${cx},${cy} L${node.x},${node.y}`}
                  />
                </circle>
              )}
              {/* Reverse packet (data coming back) for online nodes */}
              {node.env.status === "ready" && (
                <circle r="1.5" fill="rgba(237,106,255,0.5)">
                  <animateMotion
                    dur={`${3 + Math.random() * 2}s`}
                    repeatCount="indefinite"
                    path={`M${node.x},${node.y} L${cx},${cy}`}
                  />
                </circle>
              )}
            </g>
          );
        })}

        {/* Center hub */}
        <g filter="url(#moriarty-hub-glow)" className="moriarty-hub-pulse">
          <circle
            cx={cx}
            cy={cy}
            r="28"
            fill="rgba(237,106,255,0.08)"
            stroke="rgba(237,106,255,0.3)"
            strokeWidth="2"
          />
          <circle
            cx={cx}
            cy={cy}
            r="18"
            fill="rgba(237,106,255,0.12)"
            stroke="rgba(237,106,255,0.5)"
            strokeWidth="1.5"
          />
        </g>
      </svg>

      {/* HTML overlay — Hub icon */}
      <div
        className="pointer-events-none absolute z-10 flex items-center justify-center"
        style={{
          left: cx - 16,
          top: cy - 16,
          width: 32,
          height: 32,
        }}
      >
        <Skull size={18} className="text-glow-magenta text-cyber-magenta" />
      </div>

      {/* HTML overlay — Environment nodes */}
      {nodes.map((node) => {
        const meta = statusMeta(getMoriartyDisplayStatus(node.env));
        const isHovered = hoveredId === node.env.id;
        const shortId = node.env.id.replace("moriarty-", "");

        return (
          <div
            key={node.env.id}
            className="absolute z-20"
            style={{
              left: node.x - 36,
              top: node.y - 28,
              width: 72,
            }}
          >
            {/* Node circle + info */}
            <button
              onClick={() => onSelect(node.env.id)}
              onMouseEnter={() => setHoveredId(node.env.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={clsx(
                "group flex w-full flex-col items-center gap-0.5 transition-transform duration-200",
                isHovered && "scale-110",
              )}
            >
              {/* Outer ring */}
              <div
                className={clsx(
                  "relative flex h-11 w-11 items-center justify-center rounded-full border-2 transition-all duration-300",
                  meta.border,
                  node.env.status === "starting" && "animate-neon-pulse",
                  meta.glowClass,
                )}
                style={{ backgroundColor: meta.hexDim }}
              >
                <Cat size={16} className={clsx(meta.color, "drop-shadow-sm transition-colors")} />
                {/* Status dot */}
                <div
                  className={clsx(
                    "border-cyber-bg absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full border-2",
                    meta.dotColor,
                    node.env.status === "starting" && "animate-neon-pulse",
                  )}
                />
                <ProntoTopologyBadge environment={node.env} pronto={pronto} />
              </div>

              {/* Label */}
              <span className="text-cyber-text/80 mt-0.5 max-w-full truncate font-mono text-[9px] leading-tight">
                {shortId}
              </span>
              <span className="text-cyber-muted/50 flex max-w-full items-center gap-0.5 truncate text-[8px] leading-tight">
                <GitBranch size={7} />
                {node.env.branch.length > 10
                  ? node.env.branch.slice(0, 10) + "..."
                  : node.env.branch}
              </span>
            </button>

            {/* Hover tooltip card */}
            {isHovered && (
              <div className="moriarty-modal-glow border-cyber-border bg-cyber-surface pointer-events-none absolute top-full left-1/2 z-50 mt-2 w-48 -translate-x-1/2 rounded-lg border p-2.5 shadow-xl">
                <div className="mb-1.5 flex items-center gap-2">
                  <div className={clsx("h-2 w-2 rounded-full", meta.dotColor)} />
                  <span
                    className={clsx("text-[10px] font-bold tracking-wider uppercase", meta.color)}
                  >
                    {meta.label}
                  </span>
                </div>
                <div className="text-cyber-text mb-1 font-mono text-[10px]">{node.env.id}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[9px]">
                  <div className="text-cyber-muted flex items-center gap-1">
                    <GitBranch size={8} /> {node.env.branch}
                  </div>
                  <div className="text-cyber-muted flex items-center gap-1">
                    <Server size={8} /> {node.env.instanceType}
                  </div>
                  <div className="text-cyber-muted flex items-center gap-1">
                    <Clock size={8} /> {formatUptime(node.env.uptimeMinutes)}
                  </div>
                  <div className="text-cyber-muted flex items-center gap-1">
                    <DollarSign size={8} /> {formatCost(node.env.estimatedCostUsd)}
                  </div>
                </div>
                {node.env.ticketId && (
                  <div className="text-cyber-magenta/70 mt-1.5 text-[9px]">{node.env.ticketId}</div>
                )}
                <ProntoDelivery environment={node.env} pronto={pronto} compact />
                {node.env.errorMessage && (
                  <div className="text-cyber-red/70 mt-1.5 truncate text-[9px]">
                    {node.env.errorMessage}
                  </div>
                )}
                <div className="text-cyber-muted/40 mt-1.5 text-[8px]">by {node.env.createdBy}</div>
              </div>
            )}
          </div>
        );
      })}

      {/* Legend */}
      <div className="text-cyber-muted/60 absolute bottom-3 left-3 z-10 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px]">
        <span className="text-cyber-muted/40 tracking-wider uppercase">Environment</span>
        <span className="flex items-center gap-1">
          <span className="bg-cyber-green h-1.5 w-1.5 rounded-full" /> Online
        </span>
        <span className="flex items-center gap-1">
          <span className="bg-cyber-yellow h-1.5 w-1.5 rounded-full" /> Booting / warning
        </span>
        <span className="flex items-center gap-1">
          <span className="bg-cyber-red h-1.5 w-1.5 rounded-full" /> Failed
        </span>
        <span className="border-cyber-border/60 h-3 border-l" />
        <span className="text-cyber-muted/40 tracking-wider uppercase">PR</span>
        <span className="text-cyber-yellow flex items-center gap-1">
          <GitPullRequest size={9} /> Waiting
        </span>
        <span className="text-cyber-red flex items-center gap-1">
          <GitPullRequest size={9} /> Blocked
        </span>
        <span className="text-cyber-green flex items-center gap-1">
          <GitPullRequest size={9} /> Ready to merge
        </span>
      </div>

      {/* Active count badge */}
      <div className="text-cyber-muted/40 absolute top-3 right-3 z-10 text-[9px]">
        {environments.length} Moriarty{environments.length !== 1 ? "s" : ""} deployed
      </div>
    </div>
  );
}

function prontoStatusClasses(status: MoriartyProntoStatus["dashboardStatus"]): string {
  if (status === "ready_to_merge") {
    return "border-cyber-green/30 bg-cyber-green/10 text-cyber-green";
  }
  if (status === "blocked_by_changes" || status === "blocked_by_merge_readiness") {
    return "border-cyber-red/30 bg-cyber-red/10 text-cyber-red";
  }
  if (status === "special_case") {
    return "border-cyber-border bg-cyber-muted/10 text-cyber-muted";
  }
  return "border-cyber-yellow/30 bg-cyber-yellow/10 text-cyber-yellow";
}

function ProntoTopologyBadge({
  environment,
  pronto,
}: {
  environment: MoriartyEnvironment;
  pronto: ProntoEnrichment;
}) {
  if (!isMoriartyProntoEligible(environment)) return null;

  const status = environment.prNumber ? pronto.byPrNumber.get(environment.prNumber) : undefined;
  const degraded = pronto.source?.status === "degraded";
  const title = status
    ? `PR #${environment.prNumber}: ${prontoStatusDetails(status, pronto.source)}`
    : degraded
      ? `PR #${environment.prNumber}: ${pronto.source?.warning ?? "Pronto data may be stale"}`
      : pronto.isLoading
        ? `PR #${environment.prNumber}: checking Pronto`
        : pronto.isError
          ? `PR #${environment.prNumber}: Pronto unavailable`
          : `PR #${environment.prNumber}: not in Pronto's open queue`;

  return (
    <span
      className={clsx(
        "border-cyber-bg absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border",
        status
          ? prontoStatusClasses(status.dashboardStatus)
          : degraded
            ? "bg-cyber-bg text-cyber-yellow"
            : "bg-cyber-bg text-cyber-magenta/70",
      )}
      title={title}
      aria-label={title}
    >
      {degraded ? <AlertTriangle size={8} /> : <GitPullRequest size={8} />}
    </span>
  );
}

function prontoStatusDetails(
  status: MoriartyProntoStatus,
  source: MoriartyProntoSource | null,
): string {
  return [
    status.dashboardSummary,
    status.missingApprovals.length > 0
      ? `Missing approvals: ${status.missingApprovals.join(", ")}`
      : null,
    status.blocker.description,
    status.mergeReadiness.summary,
    source?.warning,
  ]
    .filter((detail): detail is string => Boolean(detail))
    .join(" · ");
}

function ProntoDelivery({
  environment,
  pronto,
  compact = false,
}: {
  environment: MoriartyEnvironment;
  pronto: ProntoEnrichment;
  compact?: boolean;
}) {
  if (!isMoriartyProntoEligible(environment)) return null;

  const textClass = compact ? "text-[9px]" : "text-[10px]";
  const spacingClass = compact ? "mt-1.5" : undefined;
  if (pronto.isLoading) {
    return (
      <span className={clsx("text-cyber-muted/50", textClass, spacingClass)}>
        Checking Pronto...
      </span>
    );
  }
  if (pronto.isError) {
    return (
      <span
        className={clsx("text-cyber-muted/60", textClass, spacingClass)}
        title="Pronto is unavailable"
      >
        Pronto unavailable
      </span>
    );
  }

  const status = environment.prNumber ? pronto.byPrNumber.get(environment.prNumber) : undefined;
  if (!status) {
    if (pronto.source?.status === "degraded") {
      return (
        <span
          className={clsx(
            "text-cyber-yellow/70 inline-flex items-center gap-1",
            textClass,
            spacingClass,
          )}
          title={pronto.source.warning ?? "Pronto's GitHub data may be stale"}
        >
          <AlertTriangle size={9} />
          Pronto data stale
        </span>
      );
    }
    return (
      <span
        className={clsx("text-cyber-muted/50", textClass, spacingClass)}
        title="This PR was not returned in Pronto's current open-PR queue"
      >
        Not in open queue
      </span>
    );
  }

  const degraded = pronto.source?.status === "degraded";
  return (
    <span
      className={clsx("inline-flex flex-col items-start gap-0.5", spacingClass)}
      title={prontoStatusDetails(status, pronto.source)}
    >
      <span
        className={clsx(
          "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5",
          textClass,
          prontoStatusClasses(status.dashboardStatus),
        )}
      >
        {degraded && <AlertTriangle size={9} />}
        {status.dashboardLabel}
      </span>
      {status.missingApprovals.length > 0 && (
        <span className="text-cyber-muted/70 text-[9px]">
          Missing: {status.missingApprovals.join(", ")}
        </span>
      )}
    </span>
  );
}

// ── Table View (All / Terminated tabs) ──────────────────────

type SortKey = "id" | "status" | "branch" | "uptime" | "cost" | "createdBy";

function EnvironmentTable({
  environments,
  pronto,
  onSelect,
}: {
  environments: MoriartyEnvironment[];
  pronto: ProntoEnrichment;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("status");
  const [sortAsc, setSortAsc] = useState(true);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === "id" || key === "status" || key === "branch" || key === "createdBy");
    }
  }

  const filtered = useMemo(() => {
    let list = environments;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.id.toLowerCase().includes(q) ||
          e.branch.toLowerCase().includes(q) ||
          e.createdBy.toLowerCase().includes(q) ||
          (e.prNumber !== null && String(e.prNumber).includes(q)) ||
          (e.ticketId && e.ticketId.toLowerCase().includes(q)),
      );
    }

    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "id":
          cmp = a.id.localeCompare(b.id);
          break;
        case "status":
          cmp = compareMoriartyStatuses(a.status, b.status);
          break;
        case "branch":
          cmp = a.branch.localeCompare(b.branch);
          break;
        case "uptime":
          cmp = a.uptimeMinutes - b.uptimeMinutes;
          break;
        case "cost":
          cmp = a.estimatedCostUsd - b.estimatedCostUsd;
          break;
        case "createdBy":
          cmp = a.createdBy.localeCompare(b.createdBy);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });

    return list;
  }, [environments, search, sortKey, sortAsc]);

  if (environments.length === 0) {
    return (
      <div className="py-12 text-center">
        <Cat size={28} className="text-cyber-muted/15 mx-auto mb-3" />
        <p className="text-cyber-muted text-sm">No environments found</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Search */}
      <div className="relative mb-3">
        <Search
          size={14}
          className="text-cyber-muted/50 absolute top-1/2 left-3 -translate-y-1/2"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by ID, branch, ticket, PR, or creator..."
          className="border-cyber-border bg-cyber-surface text-cyber-text placeholder-cyber-muted/50 focus:border-cyber-magenta/30 w-full rounded border py-2 pr-3 pl-9 text-xs focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="border-cyber-border overflow-hidden rounded-lg border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-cyber-border bg-cyber-surface/80 border-b">
              <SortHeader
                label="Moriarty"
                sortKey="id"
                currentSort={sortKey}
                asc={sortAsc}
                onSort={handleSort}
              />
              <SortHeader
                label="Status"
                sortKey="status"
                currentSort={sortKey}
                asc={sortAsc}
                onSort={handleSort}
              />
              <SortHeader
                label="Branch"
                sortKey="branch"
                currentSort={sortKey}
                asc={sortAsc}
                onSort={handleSort}
              />
              <SortHeader
                label="Uptime"
                sortKey="uptime"
                currentSort={sortKey}
                asc={sortAsc}
                onSort={handleSort}
              />
              <SortHeader
                label="Cost"
                sortKey="cost"
                currentSort={sortKey}
                asc={sortAsc}
                onSort={handleSort}
              />
              <SortHeader
                label="Creator"
                sortKey="createdBy"
                currentSort={sortKey}
                asc={sortAsc}
                onSort={handleSort}
              />
              <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">PR</th>
              <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">Delivery</th>
              <th className="text-cyber-muted/70 px-3 py-2 text-left font-normal">Ticket</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((env) => {
              const meta = statusMeta(getMoriartyDisplayStatus(env));
              return (
                <tr
                  key={env.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open environment ${env.id}`}
                  onClick={() => onSelect(env.id)}
                  onKeyDown={(event) => {
                    if (event.currentTarget !== event.target) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(env.id);
                    }
                  }}
                  className="border-cyber-border/50 hover:bg-cyber-magenta/5 cursor-pointer border-b transition-colors"
                >
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className={clsx("h-2 w-2 flex-shrink-0 rounded-full", meta.dotColor)} />
                      <span className="text-cyber-text font-mono">
                        {env.id.replace("moriarty-", "")}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={clsx(
                        "rounded-full border px-1.5 py-0.5 text-[10px] tracking-wider uppercase",
                        meta.bg,
                        meta.color,
                        meta.border,
                      )}
                    >
                      {meta.label}
                    </span>
                  </td>
                  <td className="text-cyber-muted px-3 py-2.5 font-mono">{env.branch}</td>
                  <td className="text-cyber-muted px-3 py-2.5">
                    {formatUptime(env.uptimeMinutes)}
                  </td>
                  <td className="text-cyber-muted px-3 py-2.5">
                    {formatCost(env.estimatedCostUsd)}
                  </td>
                  <td className="text-cyber-muted max-w-[120px] truncate px-3 py-2.5">
                    {env.createdBy}
                  </td>
                  <td className="px-3 py-2.5">
                    {env.prUrl && (
                      <a
                        href={env.prUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-cyber-magenta inline-flex items-center gap-1 text-[10px] hover:underline"
                        aria-label={
                          env.prNumber ? `Open pull request ${env.prNumber}` : "Open pull request"
                        }
                      >
                        <GitPullRequest size={10} />
                        {env.prNumber ? `#${env.prNumber}` : "PR"}
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <ProntoDelivery environment={env} pronto={pronto} />
                  </td>
                  <td className="px-3 py-2.5">
                    {env.ticketId &&
                      (env.ticketUrl ? (
                        <a
                          href={env.ticketUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-cyber-magenta text-[10px] hover:underline"
                        >
                          {env.ticketId}
                        </a>
                      ) : (
                        <span className="text-cyber-muted text-[10px]">{env.ticketId}</span>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-cyber-muted py-6 text-center text-xs">
            No results for &quot;{search}&quot;
          </div>
        )}
      </div>
      <div className="text-cyber-muted/50 mt-2 px-1 text-[10px]">
        {filtered.length} of {environments.length} environment
        {environments.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  currentSort,
  asc,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  asc: boolean;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return (
    <th
      className="hover:text-cyber-text cursor-pointer px-3 py-2 text-left font-normal transition-colors"
      onClick={() => onSort(sortKey)}
    >
      <span
        className={clsx(
          "flex items-center gap-1",
          isActive ? "text-cyber-magenta" : "text-cyber-muted/70",
        )}
      >
        {label}
        <ArrowUpDown
          size={10}
          className={isActive ? "text-cyber-magenta" : "text-cyber-muted/30"}
        />
        {isActive && <span className="text-[8px]">{asc ? "ASC" : "DESC"}</span>}
      </span>
    </th>
  );
}
